import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useRouterParams } from 'src/hooks/utils';
import Loader from 'src/components/Loader';
import copy from 'copy-to-clipboard';
import { SurveyDashboardPropTypes } from 'src/components/SurveyDashboard/propTypes';
import SurveyDashboardOverview from 'src/components/SurveyDashboard/SurveyDashboardOverview';
import SurveyDashboardQuestionCharts from 'src/components/SurveyDashboard/SurveyDashboardQuestionCharts';
import JobSurveysDropdown from 'src/components/JobSurveysDropdown/';
import { notification } from 'src/components/Notification';
import { SURVEY_RESPONSES_BY_VALIDITY } from 'src/components/SurveyDashboard/constants';
import s from './SurveyDashboard.styles';

const SurveyDashboard = ({ match, job, datasheets, getDashboardData, isPublic }) => {
  const { t } = useTranslation();
  const { code } = match ? useRouterParams(match) : {code: ''};
  const [selectedSurvey, setSelectedSurvey] = useState();
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [selectedOptionValue, setSelectedOptionValue] = useState(SURVEY_RESPONSES_BY_VALIDITY[0]);

  const [surveySummaryCallback,
    {
      data: surveySummaryData,
      loading: surveySummaryDataLoading,
    },
  ] = getDashboardData({});

  const formatFromDate = (date) => {
    const tmpMoment = moment(date).toDate();

    return `${tmpMoment.getFullYear()}-${tmpMoment.getMonth()+1}-${tmpMoment.getDate()} 00:00:00`;
  };

  const formatToDate = (date) => {
    const tmpMoment = moment(date).toDate();

    return `${tmpMoment.getFullYear()}-${tmpMoment.getMonth()+1}-${tmpMoment.getDate()} 23:59:59`;
  };

  const onSelectedSurveyChange = (survey) => {
    if (survey) {
      const { jobId, datasheetVersionId } = survey;
      setSelectedSurvey(survey);
      setFrom(null);
      setTo(null);
      setSelectedOptionValue(SURVEY_RESPONSES_BY_VALIDITY[0]);
      surveySummaryCallback({ variables: { jobId, datasheetVersionId } });
    }
  };

  useEffect(()=>{
    if(isPublic){
      surveySummaryCallback({ variables: { code } });
    }
  },[]);

  const onFilterChange = ({ selectedFromDate, selectedToDate, excludeInvalidResponse = false}) => {
    const { jobId, datasheetVersionId } = selectedSurvey || {};
    const variables = isPublic ? { code } : { jobId, datasheetVersionId };
    surveySummaryCallback({ 
      variables: { 
        ...variables, 
        fromDate: selectedFromDate ? formatFromDate(selectedFromDate) : null, 
        toDate: selectedToDate ? formatToDate(selectedToDate) : null, 
        excludeInvalidResponse,
      }
    });
  };

  return (
    <s.SurveySummaryWrapper isPublic>
      <s.Row>
        <s.JobNumberLabel>{isPublic ? surveySummaryData?.surveySummaryByCode?.reportNumber : job.reportNumber}</s.JobNumberLabel>
      </s.Row>
      {!isPublic && <s.Row>
        <s.Left>
          <s.SurveyLabel>{t('SURVEY_LABEL')}</s.SurveyLabel>
          <JobSurveysDropdown
            datasheets={datasheets}
            onChange={onSelectedSurveyChange}
          />
        </s.Left>
        { selectedSurvey?.surveyData?.dashboardLink && <s.Right>
          {t('SHARE_LINK')}
          <s.ShareIcon onClick={() => {
            copy(selectedSurvey.surveyData.dashboardLink);
            notification.success(t('LINK_COPIED'));
          }} name={'share'}/>
        </s.Right>
        }
      </s.Row>}
        <>
          <SurveyDashboardOverview
            surveySummary={isPublic ? surveySummaryData?.surveySummaryByCode : surveySummaryData?.surveySummary}
            from={from}
            to={to}
            setFrom={setFrom}
            setTo={setTo}
            onFilterChange={onFilterChange}
            hide={surveySummaryDataLoading && !surveySummaryData}
            isPublic={isPublic}
            selectedOptionValue={selectedOptionValue}
            setSelectedOptionValue={setSelectedOptionValue}
          />
          {!surveySummaryDataLoading && surveySummaryData && (
            <SurveyDashboardQuestionCharts
              surveySummary={isPublic ? surveySummaryData?.surveySummaryByCode : surveySummaryData?.surveySummary}
            />
          )}
        </>
      {surveySummaryDataLoading && <Loader />}
    </s.SurveySummaryWrapper>
  );
};

SurveyDashboard.defaultProps = SurveyDashboardPropTypes;

export default React.memo(SurveyDashboard);
